import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Button,
  ErrorPage,
  FeedbackState,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
} from "@h2j-data-team/ui";

import { BackLink } from "@/components/back-link";
import { CompareCategoriesGrid } from "@/components/app-analysis/CompareCategoriesGrid";
import { CompareFindingsList } from "@/components/app-analysis/CompareFindingsList";
import { CompareItemChanges } from "@/components/app-analysis/CompareItemChanges";
import { CompareSummaryCards } from "@/components/app-analysis/CompareSummaryCards";
import { supportCode } from "@/components/error-screen";
import {
  appAnalysisCompareQueryOptions,
  appAnalysisDetailQueryOptions,
} from "@/features/app-analysis/api";
import { connectionQueryOptions } from "@/features/qlik-connections/api";
import { useFormatDate } from "@/hooks/use-format-date";

export const Route = createFileRoute("/_authenticated/analyses/$analysisId_/compare/$baseId")({
  staticData: { pageTitle: "breadcrumbs.analyses", breadcrumb: "breadcrumbs.analysisCompare" },
  component: AnalysisComparePage,
});

function AnalysisComparePage() {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const { formatDateTime } = useFormatDate();
  const { analysisId, baseId } = Route.useParams();

  const diffQuery = useQuery(appAnalysisCompareQueryOptions(analysisId, baseId));
  const currentQuery = useQuery(appAnalysisDetailQueryOptions(analysisId));
  const baseQuery = useQuery(appAnalysisDetailQueryOptions(baseId));
  const { data: connection } = useQuery(
    connectionQueryOptions(currentQuery.data?.connectionId ?? ""),
  );

  const back = (
    <BackLink
      to="/analyses/$analysisId"
      params={{ analysisId }}
      label={t("compare.backToAnalysis")}
    />
  );

  if (diffQuery.isLoading || currentQuery.isLoading || baseQuery.isLoading) {
    return <PageSkeleton variant="detail" label={t("detail.loadingAnalysis")} />;
  }

  const error = diffQuery.error ?? currentQuery.error ?? baseQuery.error;
  const diff = diffQuery.data;
  const current = currentQuery.data;
  const base = baseQuery.data;

  if (error || !diff || !current || !base) {
    return (
      <ErrorPage
        kind="load-failed"
        header={
          <PageHeader eyebrow={t("compare.eyebrow")} title={t("detail.errorTitle")} back={back} />
        }
        title={t("compare.error")}
        supportCode={supportCode(error, tc("error.supportCode"))}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              for (const query of [diffQuery, currentQuery, baseQuery]) {
                if (query.error) void query.refetch();
              }
            }}
          >
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  // The service orders the pair by `analyzedAt`; follow its verdict rather than
  // assuming the route's :id is the newer one.
  const from = diff.fromAnalysisId === current.id ? current : base;
  const to = diff.toAnalysisId === current.id ? current : base;
  const fromSummary = from.summary;
  const toSummary = to.summary;

  const header = (
    <PageHeader
      eyebrow={t("compare.eyebrow")}
      title={to.appName}
      back={back}
      meta={
        <span className="truncate font-mono">
          {[
            t("compare.kpi.range", {
              from: formatDateTime(from.analyzedAt),
              to: formatDateTime(to.analyzedAt),
            }),
            connection?.name,
            to.spaceName,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      }
      actions={
        <>
          <Button variant="outline" size="sm" asChild>
            <Link to="/analyses/$analysisId" params={{ analysisId: from.id }}>
              {t("compare.openBase")}
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/analyses/$analysisId" params={{ analysisId: to.id }}>
              {t("compare.openCurrent")}
            </Link>
          </Button>
        </>
      }
    />
  );

  // Defensive: a completed analysis always carries a summary, but the schema
  // allows it to be absent (placeholder rows).
  if (!fromSummary || !toSummary) {
    return (
      <PageShell header={header}>
        <PageContent>
          <FeedbackState state="empty" title={t("detail.resultUnavailable")} />
        </PageContent>
      </PageShell>
    );
  }

  return (
    <PageShell header={header}>
      <PageContent className="space-y-6">
        <CompareSummaryCards
          summaryDelta={diff.summaryDelta}
          fromSummary={fromSummary}
          toSummary={toSummary}
          fromHealth={from.health ?? null}
          toHealth={to.health ?? null}
        />

        {diff.healthCategoryDeltas && (
          <CompareCategoriesGrid
            deltas={diff.healthCategoryDeltas}
            fromHealth={from.health ?? null}
            toHealth={to.health ?? null}
          />
        )}

        <CompareFindingsList findings={diff.findings} />

        <CompareItemChanges fieldChanges={diff.fieldChanges} itemChanges={diff.itemChanges} />
      </PageContent>
    </PageShell>
  );
}
